
import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/components/ui/use-toast";
import { useQuery } from "@tanstack/react-query";
import { 
  ArrowLeft, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Info,
  CreditCard,
  User,
  Calendar,
  Briefcase
} from "lucide-react";

// Mock data for a single application
const getMockApplication = (id) => {
  return {
    id,
    borrower_name: "Alice Johnson",
    amount: 15000,
    credit_score: "720",
    application_date: "2023-10-25",
    status: "review",
    loan_purpose: "Home renovation",
    loan_term: "36 months",
    interest_rate: "4.5%",
    monthly_income: "$5,200",
    debt_to_income: "32%",
    employment_status: "Full-time",
    employer: "Tech Solutions Inc.",
    address: "123 Main St, Anytown, CA 12345",
    phone: "(555) 123-4567",
    email: "alice@example.com"
  };
};

// Risk scores component
const RiskScoreIndicator = ({ score, label }) => {
  const getScoreColor = () => {
    if (score >= 80) return "text-green-500";
    if (score >= 60) return "text-yellow-500";
    return "text-red-500";
  };

  const getProgressColor = () => {
    if (score >= 80) return "bg-green-500";
    if (score >= 60) return "bg-yellow-500";
    return "bg-red-500";
  };

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <span className="text-sm font-medium">{label}</span>
        <span className={`text-sm font-bold ${getScoreColor()}`}>{score}/100</span>
      </div>
      <Progress value={score} className={getProgressColor()} />
    </div>
  );
};

const ApplicationReview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [decision, setDecision] = useState(null);
  const [notes, setNotes] = useState("");

  const { data: application, isLoading } = useQuery({
    queryKey: ['application-review', id],
    queryFn: async () => {
      try {
        // In a real implementation, this would fetch data from Supabase
        // For demo purposes, we'll return mock data
        return getMockApplication(id);
      } catch (error) {
        console.error('Error fetching application details:', error);
        toast({
          title: "Error",
          description: "Failed to load application details",
          variant: "destructive",
        });
        return null;
      }
    }
  });

  const handleSubmitReview = () => {
    if (!decision) {
      toast({
        title: "Error",
        description: "Please select a decision",
        variant: "destructive",
      });
      return;
    }

    // In a real application, this would submit the decision to the backend
    toast({
      title: "Success",
      description: `Application ${decision === "approve" ? "approved" : "declined"}`,
      variant: "default",
    });

    // Navigate back to applications list
    navigate("/lender/loans/applications");
  };

  if (isLoading || !application) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center">
        <Button
          variant="ghost"
          className="flex items-center gap-2"
          onClick={() => navigate(`/lender/loans/applications/${id}`)}
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Application
        </Button>
      </div>

      <div className="flex flex-col md:flex-row justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Review Application</h1>
          <p className="text-gray-500">
            Application #{id} | {application.borrower_name}
          </p>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/lender/loans/applications/${id}`)}>
            Cancel
          </Button>
          <Button onClick={handleSubmitReview}>Submit Decision</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Loan Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <CreditCard className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Amount</p>
                    <p className="font-semibold">${application.amount.toLocaleString()}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Calendar className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Term</p>
                    <p className="font-semibold">{application.loan_term}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <User className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Credit Score</p>
                    <p className="font-semibold">{application.credit_score}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Briefcase className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Purpose</p>
                    <p className="font-semibold">{application.loan_purpose}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Application Assessment</CardTitle>
              <CardDescription>Please review and make a decision</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <h3 className="font-medium">Decision</h3>
                <RadioGroup value={decision} onValueChange={setDecision}>
                  <div className="flex items-start space-x-2">
                    <RadioGroupItem value="approve" id="approve" />
                    <div className="grid gap-1.5">
                      <Label htmlFor="approve" className="flex items-center gap-2">
                        <CheckCircle className="h-5 w-5 text-green-500" />
                        Approve
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Approve this loan application and proceed with the lending process.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-2 mt-3">
                    <RadioGroupItem value="decline" id="decline" />
                    <div className="grid gap-1.5">
                      <Label htmlFor="decline" className="flex items-center gap-2">
                        <XCircle className="h-5 w-5 text-red-500" />
                        Decline
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Decline this loan application due to not meeting requirements.
                      </p>
                    </div>
                  </div>
                </RadioGroup>
              </div>

              <Separator />

              <div className="space-y-4">
                <h3 className="font-medium">Review Notes</h3>
                <Textarea
                  placeholder="Add notes about your decision..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={5}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Risk Assessment</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <RiskScoreIndicator score={85} label="Credit Profile" />
              <RiskScoreIndicator score={72} label="Income Verification" />
              <RiskScoreIndicator score={90} label="Employment Stability" />
              <RiskScoreIndicator score={65} label="Debt-to-Income Ratio" />

              <Separator />
              
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Overall Risk Score</span>
                  <span className="text-sm font-bold text-green-500">78/100</span>
                </div>
                <Progress value={78} className="bg-green-500" />
              </div>

              <div className="p-3 bg-blue-50 text-blue-800 rounded-md flex items-start gap-2">
                <Info className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <p className="text-sm">
                  This application has been pre-assessed and shows a low risk profile.
                  Income verification is complete and credit score is above minimum thresholds.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Documents Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm">ID Verification</p>
                  <span className="text-green-500 flex items-center">
                    <CheckCircle className="h-4 w-4 mr-1" /> Verified
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-sm">Proof of Income</p>
                  <span className="text-green-500 flex items-center">
                    <CheckCircle className="h-4 w-4 mr-1" /> Verified
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-sm">Bank Statements</p>
                  <span className="text-yellow-500 flex items-center">
                    <AlertTriangle className="h-4 w-4 mr-1" /> Under Review
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ApplicationReview;
